/**
 * 💬 [사용 문의] 단추 (v2.275.0 · 2026-10-19 사용자 「챗봇 버튼 누르면 팝업으로 채팅창」)
 *
 * 오른쪽 위 모서리 묶음(`CornerActions`)에 [📮 개발요청] 과 나란히 선다 — 포털 껍데기가 한 번만 그린다.
 *   · 누르면 채팅 창(HelpBotPanel · 처음 누를 때 내려받는다) · 다시 누르거나 ✕ 로 닫는다(대화는 남는다)
 *   · 안 본 답이 있으면 빨간 점 — 창을 닫은 채 기다리면 10분까지 15초마다 상태를 본다(그 뒤엔 창을 열거나 화면에 돌아올 때)
 *   · 지금 대화 번호와 적던 질문은 여기서 들고 있다 — 창을 닫았다 열어도, 다른 메뉴로 옮겨도 같은 대화·글이 이어진다
 */

import { lazy, Suspense, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import * as hb from '../lib/helpbot-api';

const HelpBotPanel = lazy(() =>
  import('./HelpBotPanel').then((m) => ({ default: m.HelpBotPanel })),
);

export function HelpBotFab() {
  const [open, setOpen] = useState(false);
  const [threadId, setThreadId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const stateQ = useQuery({
    queryKey: ['helpbot', 'state'],
    queryFn: hb.getState,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (!open && q.state.data?.open ? hb.fabPollMs(q.state.data.open.askedAt) : false),
  });
  const unread = stateQ.data?.unread ?? 0;
  const waiting = !!stateQ.data?.open;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="사용 문의 — 포털 쓰는 법이나 오류처럼 보이는 것을 물어보면 도우미가 1~2분 안에 답합니다"
        aria-label={unread > 0 ? `사용 문의 (안 본 답 ${unread})` : '사용 문의'}
        aria-expanded={open}
        className={[
          'pointer-events-auto relative font-medium',
          // 폰: 둥근 48px(개발요청 단추 위)
          'w-12 h-12 rounded-full bg-indigo-600 text-white text-xl shadow-lg flex items-center justify-center',
          // PC(md 이상): 창 맨 위 가장자리에 붙은 작은 탭
          'md:w-auto md:h-6 md:px-2.5 md:rounded-none md:rounded-b',
          'md:border md:border-t-0 md:border-indigo-300 md:bg-white md:text-indigo-800 md:text-xs md:shadow-sm',
          'md:hover:bg-indigo-50 md:hover:border-indigo-400',
          open ? 'md:bg-indigo-50' : '',
        ].join(' ')}
      >
        <span className="md:hidden" aria-hidden="true">💬</span>
        <span className="hidden md:inline">💬 사용 문의{waiting && !open ? ' ⏳' : ''}</span>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white md:-right-1 md:top-auto md:-bottom-1 md:h-2.5 md:w-2.5" aria-hidden="true" />
        )}
      </button>
      {/* 창은 body 로 — 모서리 묶음(fixed z-30 · 누름 통과)의 쌓임 층·pointer-events 를 물려받지 않게 */}
      {open && createPortal(
        <Suspense fallback={null}>
          <HelpBotPanel threadId={threadId} setThreadId={setThreadId} text={draft} setText={setDraft} onClose={() => setOpen(false)} />
        </Suspense>,
        document.body,
      )}
    </>
  );
}
